import axios from "axios";
import React, { useState, useEffect, useRef } from "react";
import { Chart } from "chart.js/auto";
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { baseUrl } from "../Univ";

function ManufacturingDashboard(){

  const today = new Date().toISOString().substring(0,10);

  // KPI States
  const [productCount,setProductCount] = useState(0);
  const [bomCount,setBomCount] = useState(0);
  const [machineCount,setMachineCount] = useState(0);
  const [productionCount,setProductionCount] = useState(0);
  const [todayProduction,setTodayProduction] = useState(0);
  const [breakdownCount,setBreakdownCount] = useState(0);
  const [pendingPM,setPendingPM] = useState(0);
  const [lowSpare,setLowSpare] = useState(0);

  const [productionEntries,setProductionEntries] = useState([]);
  const [breakdowns,setBreakdowns] = useState([]);
  const [workcenters,setWorkcenters] = useState([]);
  const [maintenances,setMaintenances] = useState([]);
  const [productionOrders,setProductionOrders] = useState([]);

  const prodChartRef = useRef();
  const downChartRef = useRef();
  const wcChartRef = useRef();
  const costChartRef = useRef();

  // ===============================
  // FETCH DATA
  // ===============================
  useEffect(()=>{
    fetchCounts();
    fetchChartsData();
  },[]);

  function fetchCounts(){
    axios.get(baseUrl+"products/").then(res=>setProductCount(res.data.length));
    axios.get(baseUrl+"bom/").then(res=>setBomCount(res.data.length));
    axios.get(baseUrl+"machines/").then(res=>setMachineCount(res.data.length));
    axios.get(baseUrl+"production-orders/").then(res=>{
      setProductionCount(res.data.length);
      const todayProd = res.data.filter(p=>p.start_date === today);
      setTodayProduction(todayProd.length);
      setProductionOrders(res.data);
    });
    axios.get(baseUrl+"breakdowns/").then(res=>{
      setBreakdownCount(res.data.filter(b=>!b.resolved).length);
      setBreakdowns(res.data);
    });
    axios.get(baseUrl+"preventive-maintenance/").then(res=>{
      setPendingPM(res.data.filter(p=>!p.completed).length);
    });
    axios.get(baseUrl+"spare-parts/").then(res=>{
      const low = res.data.filter(s=>s.quantity <= s.reorder_level);
      setLowSpare(low.length);
    });
  }

  function fetchChartsData(){
    axios.get(baseUrl+"production-entry/").then(res=>{
      setProductionEntries(res.data);
    });
    axios.get(baseUrl+"workcenters/").then(res=>{
      setWorkcenters(res.data);
    });
    axios.get(baseUrl+"maintenance/").then(res=>{
      setMaintenances(res.data);
    });
  }

  // ===============================
  // PRODUCTION CHART
  // ===============================
  useEffect(()=>{
  if(!productionEntries.length) return;

  const machineMap = {};

  productionEntries.forEach(entry=>{
    const machine = entry.machine?.name;   // ✅ FIX HERE

    if(!machineMap[machine]){
      machineMap[machine] = { produced:0, rejected:0 };
    }

    machineMap[machine].produced += parseFloat(entry.produced_qty || 0);
    machineMap[machine].rejected += parseFloat(entry.rejected_qty || 0);
  });

  const ctx = prodChartRef.current.getContext("2d");

  new Chart(ctx,{
    type:"bar",
    data:{
      labels:Object.keys(machineMap),
      datasets:[
        {
          label:"Produced",
          data:Object.values(machineMap).map(m=>m.produced),
          backgroundColor:"rgba(54,162,235,0.6)"
        },
        {
          label:"Rejected",
          data:Object.values(machineMap).map(m=>m.rejected),
          backgroundColor:"rgba(255,99,132,0.6)"
        }
      ]
    }
  });

},[productionEntries]);

  // ===============================
  // DOWNTIME CHART
  // ===============================
useEffect(()=>{
  if(!breakdowns.length) return;

  const machineDown = {};

  breakdowns.forEach(b=>{
    const m = b.machine?.name || "Unknown";   // ✅ FIX HERE

    if(!machineDown[m]) machineDown[m] = 0;

    machineDown[m] += parseFloat(b.downtime_hours || 0);
  });

  const ctx = downChartRef.current.getContext("2d");

  const chartInstance = new Chart(ctx,{
    type:"pie",
    data:{
      labels:Object.keys(machineDown),
      datasets:[{
        data:Object.values(machineDown),
        backgroundColor:[
          "#ff6384",
          "#36a2eb",
          "#ffce56",
          "#4bc0c0",
          "#9966ff",
          "#ff9f40"
        ]
      }]
    }
  });

  return () => {
    chartInstance.destroy();   // ✅ Prevent duplicate charts
  };

},[breakdowns]);

  // ===============================
  // WORKCENTER CAPACITY
  // ===============================
  useEffect(()=>{
    if(!workcenters.length) return;

    const ctx = wcChartRef.current.getContext("2d");
    new Chart(ctx,{
      type:"bar",
      data:{
        labels:workcenters.map(w=>w.name),
        datasets:[{
          label:"Capacity per Day",
          data:workcenters.map(w=>w.capacity_per_day),
          backgroundColor:"rgba(75,192,192,0.6)"
        }]
      }
    });
  },[workcenters]);

  // ===============================
  // MAINTENANCE COST
  // ===============================
useEffect(() => {
  if (!maintenances.length) return;

  const costByMachine = {};

  maintenances.forEach(m => {
    const name = m.machine?.name || "Unknown";

    if (!costByMachine[name]) costByMachine[name] = 0;
    costByMachine[name] += parseFloat(m.cost || 0);
  });

  const ctx = costChartRef.current.getContext("2d");

  // Destroy old chart if exists (VERY IMPORTANT)
  if (costChartRef.current.chartInstance) {
    costChartRef.current.chartInstance.destroy();
  }

  const chart = new Chart(ctx, {
    type: "doughnut",
    data: {
      labels: Object.keys(costByMachine),
      datasets: [{
        data: Object.values(costByMachine),
        backgroundColor: [
          "#9966ff",
          "#ff9f40",
          "#4bc0c0",
          "#ff6384"
        ]
      }]
    }
  });

  costChartRef.current.chartInstance = chart;

}, [maintenances]);

  // ===============================
  // CALENDAR EVENTS
  // ===============================
  const events = productionOrders.map(p=>({
    title:p.order_no,
    start:p.start_date
  }));

  return(
    <section className="container mt-4">

      <h3>Manufacturing Dashboard</h3>

      {/* KPI CARDS */}
      <div className="row mt-3">
        <Kpi title="Products" value={productCount} color="primary"/>
        <Kpi title="BOMs" value={bomCount} color="info"/>
        <Kpi title="Machines" value={machineCount} color="success"/>
        <Kpi title="Production Orders" value={productionCount} color="warning"/>
        <Kpi title="Today Production" value={todayProduction} color="dark"/>
        <Kpi title="Open Breakdowns" value={breakdownCount} color="danger"/>
        <Kpi title="Pending PM" value={pendingPM} color="secondary"/>
        <Kpi title="Low Spare Parts" value={lowSpare} color="danger"/>
      </div>

      {/* CALENDAR */}
      <div className="row mt-5">
        <div className="col-md-6">
          <FullCalendar
            plugins={[dayGridPlugin,interactionPlugin]}
            initialView="dayGridMonth"
            events={events}
          />
        </div>

        <div className="col-md-6">
          <canvas ref={prodChartRef} style={{ maxWidth: "350px", maxHeight: "350px" }}></canvas>
        </div>
      </div>

      {/* CHARTS */}
      <div className="row mt-5">
      <div className="col-md-6 text-center">
      <canvas
        ref={downChartRef}
        style={{ maxWidth: "350px", maxHeight: "350px" }}
      ></canvas>
    </div>

        <div className="col-md-6">

          <canvas ref={wcChartRef} style={{ maxWidth: "350px", maxHeight: "350px" }}></canvas>
        </div>
      </div>

      <div className="row mt-5">
      <div className="col-md-6 text-center">
          <canvas
            ref={costChartRef}
            style={{ maxWidth: "350px", maxHeight: "350px" }}
          ></canvas>
        </div>
      </div>

    </section>
  )
}

function Kpi({title,value,color}){
  return(
    <div className="col-md-3 mb-3">
      <div className={`card border-${color}`}>
        <div className={`card-header bg-${color} text-white`}>
          {title}
        </div>
        <div className="card-body">
          <h4>{value}</h4>
        </div>
      </div>
    </div>
  )
}

export default ManufacturingDashboard;
